import { useReactiveVar } from '@apollo/client'
import { Box, Dialog, styled } from '@mui/material'
import { DebouncedFuncLeading } from 'lodash'
import { ReactNode, isValidElement, useEffect, useState } from 'react'
import { Carousel as ReactCarousel } from 'react-responsive-carousel'
import 'react-responsive-carousel/lib/styles/carousel.min.css'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { BucketMedia } from '../../../../graphql/generated'
import { drawerVar } from '../../../../providers/apollo/cache'
import logger from '../../../../utils/logger'
import useSelectedMedia from '../../hooks/useSelectedMedia'
import formatImageProxyUrl from '../../utils/formatImageProxyUrl'
import { RenderArrowNext, RenderArrowPrev } from './RenderButtons'
import RenderItem from './RenderItem'
import RenderThumbs from './RenderThumbs'
import Thumbnail from './Thumbnail'
import ThumbnailList from './ThumbnailList'
import Toolbar from './toolbar'

interface Props {
  items: BucketMedia[]
  fetchMore: DebouncedFuncLeading<() => void>
  hasMore: boolean
}

interface DialogProps {
  slideshow?: boolean
  focus?: boolean
}

const StyledDialog = styled(Dialog, { shouldForwardProp: (prop) => !['slideshow', 'focus'].includes(prop as string) })<DialogProps>(
  ({ slideshow, focus, fullWidth }) => ({
    width: fullWidth ? '100%' : 'calc(100% - 400px)',
    '& .MuiPaper-root': { background: slideshow || focus ? 'black' : undefined }
  })
)

const Wrapper = styled('div', { shouldForwardProp: (prop) => !['focus', 'fullScreen'].includes(prop as string) })<{
  focus: boolean
}>(({ focus }) => ({
  position: 'relative',
  height: '100vh',
  backgroundColor: focus ? '#000' : 'transparent',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  transition: 'width 225ms',
  '.carousel-root, .carousel, .slider-wrapper, .slider, .slide': {
    height: '100%',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    maxHeight: focus ? '90vh' : '80vh',
    width: '100%'
  }
}))

export default function Carousel({ items, fetchMore, hasMore }: Props) {
  const location = useLocation()
  const drawer = useReactiveVar(drawerVar)
  const { selectMedia } = useSelectedMedia()
  const navigate = useNavigate()
  const [media, setMedia] = useState<BucketMedia | null>(null)
  const params = new URLSearchParams(location.search)
  const action = params.get('action')
  const { shareId, mediaId, mode } = useParams<{ shareId: string; mediaId: string; mode: 'slideshow' }>()
  const selectedItemIndex = items.findIndex((item) => item.id === mediaId)
  const initialMedia = items.find((item) => item.id === mediaId)
  const selectedItem = selectedItemIndex === -1 ? 0 : selectedItemIndex

  useEffect(() => {
    // If state is set then do nothing else set initialMedia
    setMedia((current) => {
      if (initialMedia && (!current || current.id !== initialMedia.id)) {
        return initialMedia
      }

      return current
    })
  }, [initialMedia])

  const handleChange = (index: number, item: ReactNode) => {
    if (!isValidElement(item)) return

    if (hasMore && index > items.length - 5) {
      fetchMore()
    }

    const current = item.props.media as BucketMedia
    selectMedia(current.id, { replace: true })
    setMedia(current)

    const preloadNextMedia = items[index + 1]
    if (preloadNextMedia?.filename) {
      const preload = new Image()
      const src = formatImageProxyUrl(preloadNextMedia) as string
      preload.src = src
    }
    const path = `share/${shareId}/media/${current.id}`
    logger.debug(path)
  }

  const handleClose = () => {
    setMedia(null)
    navigate(-1)
  }

  return (
    <StyledDialog
      hideBackdrop={!!mediaId}
      fullScreen
      fullWidth={!drawer}
      open={mode === 'slideshow' || !!mediaId}
      focus={action === 'focus'}
      slideshow={mode === 'slideshow'}
    >
      <Wrapper focus={mode === 'slideshow' || action === 'focus'}>
        <Toolbar media={media} handleClose={handleClose} />
        <Box style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center' }}>
          <ReactCarousel
            axis="horizontal"
            onChange={handleChange}
            renderItem={RenderItem}
            renderThumbs={RenderThumbs}
            selectedItem={selectedItem}
            showIndicators={false}
            showStatus={false}
            autoPlay={mode === 'slideshow'}
            renderArrowPrev={RenderArrowPrev}
            renderArrowNext={RenderArrowNext}
            infiniteLoop={mode === 'slideshow'}
            showThumbs={false}
            useKeyboardArrows
            autoFocus
          >
            {items.map((item) => (
              <Thumbnail media={item} key={item.id} />
            ))}
          </ReactCarousel>
        </Box>
        <ThumbnailList
          showThumbs={action !== 'focus' && mode !== 'slideshow'}
          current={media}
          items={items}
          fetchMore={fetchMore}
          hasMore={hasMore}
        />
      </Wrapper>
    </StyledDialog>
  )
}
